<template>
  <div class="fixed inset-0 bg-gray-100 z-50 flex flex-col">
    <!-- Tutorial Header -->
    <div class="bg-wiq-green text-white px-6 py-4">
      <div class="max-w-2xl mx-auto flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="bg-white/20 rounded-lg px-3 py-1">
            <span class="text-sm font-semibold">TUTORIAL</span>
          </div>
          <span class="text-green-100">Step {{ currentSlide + 1 }} of 6</span>
        </div>
        <button
          v-if="currentSlide < 5"
          @click="$emit('complete')"
          class="text-green-100 hover:text-white font-medium text-sm"
        >
          Skip Tutorial
        </button>
      </div>
    </div>

    <!-- Slide Content -->
    <div class="flex-1 overflow-y-auto px-6 py-8">
      <div class="max-w-2xl mx-auto bg-white rounded-xl shadow-sm p-8">
        <!-- Slide 1: Finding a Wrestler -->
        <div v-if="currentSlide === 0" class="space-y-6">
          <h2 class="text-2xl font-bold text-gray-900 text-center">
            Finding a Wrestler
          </h2>
          <p class="text-gray-600 text-center">
            Wrestlers have two ways to check in:
          </p>

          <div class="grid md:grid-cols-2 gap-6">
            <!-- Option 1: Type name -->
            <div class="bg-gray-50 rounded-xl p-6 text-center">
              <div
                class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <svg
                  class="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <h3 class="font-semibold text-gray-900 mb-2">
                Search Their Name
              </h3>
              <p class="text-sm text-gray-600 mb-4">
                Wrestlers search for their name and select themselves from the
                list.
              </p>
              <img
                src="https://mktg-cdn.wrestlingiq.com/kiosk/typing_name.gif"
                alt="Typing wrestler name to search"
                class="rounded-lg w-full"
              />
            </div>

            <!-- Option 2: Scan ID -->
            <div class="bg-gray-50 rounded-xl p-6 text-center">
              <div
                class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <svg
                  class="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                  />
                </svg>
              </div>
              <h3 class="font-semibold text-gray-900 mb-2">Scan WIQ ID Card</h3>
              <p class="text-sm text-gray-600 mb-4">
                Wrestlers scan their WrestlingIQ ID card for instant lookup.
              </p>
              <img
                src="https://mktg-cdn.wrestlingiq.com/kiosk/kiosk_ios_scan.gif"
                alt="Scanning WIQ ID card with Kiosk iOS App"
                class="rounded-lg w-full"
              />
              <p v-if="!isInNativeApp" class="text-xs text-gray-500 mt-4">
                ID scanning requires the WrestlingIQ Kiosk App.
                <a
                  href="https://apps.apple.com/us/app/wrestlingiq-kiosk/id6757448670?itscg=30200&itsct=apps_box_badge&mttnsubad=6757448670"
                  class="text-blue-600 hover:underline"
                  >Download here</a
                >
              </p>
            </div>
          </div>
        </div>

        <!-- Slide 2: Selecting a Class -->
        <div v-else-if="currentSlide === 1" class="space-y-6">
          <h2 class="text-2xl font-bold text-gray-900 text-center">
            Checking In to Class
          </h2>
          <p class="text-gray-600 text-center">
            Wrestlers see their eligible classes and tap to check in.
          </p>

          <div class="space-y-4">
            <div class="bg-gray-50 rounded-xl p-6">
              <p class="text-sm text-gray-600 mb-4 text-center">
                Classes the wrestler is eligible for show a "Check-in" button.
                Ineligible classes show why they can't check in.
              </p>
              <img
                src="https://mktg-cdn.wrestlingiq.com/kiosk/check_in.gif"
                alt="Class list showing eligible and ineligible classes"
                class="rounded-lg w-full"
              />
            </div>
          </div>
        </div>

        <!-- Slide 3: Check-in Issues & Override -->
        <div v-else-if="currentSlide === 2" class="space-y-6">
          <h2 class="text-2xl font-bold text-gray-900 text-center">
            Check-in Issues & How to Override
          </h2>
          <p class="text-gray-600 text-center">
            Sometimes wrestlers can't check in. Here's what they might see:
          </p>

          <!-- Issue Type Toggles -->
          <div class="flex flex-wrap justify-center gap-2">
            <button
              v-for="issue in issueTypes"
              :key="issue.key"
              @click="slide3ActiveIssue = issue.key"
              :class="[
                'px-4 py-2 rounded-full text-sm font-medium transition',
                slide3ActiveIssue === issue.key
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
              ]"
            >
              {{ issue.label }}
            </button>
          </div>

          <!-- Issue Description & Screenshot -->
          <div class="bg-gray-50 rounded-xl p-6">
            <p class="text-sm text-gray-600 mb-4 text-center">
              {{ currentIssueDescription }}
            </p>
            <img
              :src="currentIssueGif"
              :alt="currentIssuePlaceholder"
              class="rounded-lg w-full"
            />
          </div>

          <!-- Admin Override -->
          <div
            class="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center"
          >
            <h3 class="font-semibold text-blue-900 mb-2">How to Override</h3>
            <p class="text-sm text-blue-800 mb-4">
              If a wrestler needs to check in despite an issue, you can override
              it with your admin PIN.
            </p>
            <p v-if="isFirstLaunch && adminPin" class="text-blue-900">
              Your admin PIN is:
              <span class="text-3xl font-bold block mt-2">{{ adminPin }}</span>
            </p>
            <p v-else class="text-sm text-blue-800">
              You can find your admin PIN in your team's kiosk settings on
              wrestlingiq.com.
            </p>
          </div>
        </div>

        <!-- Slide 4: Calendar & Attendees -->
        <div v-else-if="currentSlide === 3" class="space-y-6">
          <h2 class="text-2xl font-bold text-gray-900 text-center">
            Managing Who's on the Mat
          </h2>
          <p class="text-gray-600 text-center">
            View your class schedule, see who's checked in, and edit check-ins
            as needed.
          </p>

          <div class="bg-gray-50 rounded-xl p-6">
            <p class="text-sm text-gray-600 mb-4 text-center">
              Tap the calendar icon to see your class schedule and attendee
              lists. Drop-in and trial check-ins are highlighted so you can
              easily spot new visitors.
            </p>
            <img
              src="https://mktg-cdn.wrestlingiq.com/kiosk/attendee.gif"
              alt="Calendar and attendee list"
              class="rounded-lg w-full"
            />
          </div>
        </div>

        <!-- Slide 5: Settings -->
        <div v-else-if="currentSlide === 4" class="space-y-6">
          <h2 class="text-2xl font-bold text-gray-900 text-center">Settings</h2>
          <p class="text-gray-600 text-center">
            Customize your kiosk behavior from the settings menu.
          </p>

          <div class="bg-gray-50 rounded-xl p-6">
            <div class="space-y-4 mb-6">
              <div class="flex items-start gap-3">
                <div
                  class="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                >
                  <svg
                    class="w-4 h-4 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <p class="font-medium text-gray-900">AAU/USAW Requirements</p>
                  <p class="text-sm text-gray-600">
                    Toggle whether membership verification is required at
                    check-in. Must be enabled on your team account first.
                  </p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <div
                  class="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                >
                  <svg
                    class="w-4 h-4 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <p class="font-medium text-gray-900">General Check-in</p>
                  <p class="text-sm text-gray-600">
                    Enable this to allow check-ins without selecting a specific
                    class. Great for tracking general facility access.
                  </p>
                </div>
              </div>
            </div>
            <img
              src="https://mktg-cdn.wrestlingiq.com/kiosk/settings.gif"
              alt="Kiosk settings screen"
              class="rounded-lg w-full"
            />
          </div>
        </div>

        <!-- Slide 6: Branding & Reload -->
        <div v-else-if="currentSlide === 5" class="space-y-6">
          <h2 class="text-2xl font-bold text-gray-900 text-center">
            Branding & Reload
          </h2>
          <p class="text-gray-600 text-center">
            Personalize your kiosk and keep it up to date.
          </p>

          <div class="bg-gray-50 rounded-xl p-6">
            <div class="space-y-4 mb-6">
              <div class="flex items-start gap-3">
                <div
                  class="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                >
                  <svg
                    class="w-4 h-4 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
                    />
                  </svg>
                </div>
                <div>
                  <p class="font-medium text-gray-900">Custom Branding</p>
                  <p class="text-sm text-gray-600">
                    Add your team logo and colors in your team settings to brand
                    your kiosk experience.
                  </p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <div
                  class="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                >
                  <svg
                    class="w-4 h-4 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </div>
                <div>
                  <p class="font-medium text-gray-900">Reload Button</p>
                  <p class="text-sm text-gray-600">
                    Events refresh automatically every 45 minutes. For
                    immediate updates, tap Reload in the footer.
                  </p>
                </div>
              </div>
            </div>
            <img
              src="https://mktg-cdn.wrestlingiq.com/kiosk/branding_and_reload.gif"
              alt="Branding and reload demonstration"
              class="rounded-lg w-full"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation -->
    <div class="bg-white border-t border-gray-200 px-6 py-4">
      <div class="max-w-2xl mx-auto flex items-center justify-between">
        <!-- Previous Button -->
        <button
          v-if="currentSlide > 0"
          @click="currentSlide--"
          class="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Previous
        </button>
        <div v-else></div>

        <!-- Dot Indicators -->
        <div class="flex gap-2">
          <button
            v-for="(_, index) in 6"
            :key="index"
            @click="currentSlide = index"
            :class="[
              'w-2 h-2 rounded-full transition',
              currentSlide === index
                ? 'bg-gray-900'
                : 'bg-gray-300 hover:bg-gray-400',
            ]"
          />
        </div>

        <!-- Next/Done Button -->
        <button
          v-if="currentSlide < 5"
          @click="currentSlide++"
          class="flex items-center gap-2 text-gray-900 hover:text-gray-700 font-medium"
        >
          Next
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
        <button
          v-else
          @click="$emit('complete')"
          class="px-6 py-2 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition"
        >
          Get Started
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "KioskTutorial",
  props: {
    adminPin: {
      type: String,
      default: null,
    },
    isInNativeApp: {
      type: Boolean,
      default: false,
    },
    isFirstLaunch: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentSlide: 0,
      slide3ActiveIssue: "roster",
      issueTypes: [
        { key: "roster", label: "Not on Roster" },
        { key: "paused", label: "Subscription Paused" },
        { key: "overdue", label: "Subscription Overdue" },
        { key: "membership", label: "AAU/USAW Required" },
      ],
    };
  },
  computed: {
    currentIssueDescription() {
      const descriptions = {
        roster:
          "The wrestler is not assigned to the roster linked to this class. This usually indicates a problem with their registration. E.g. they are not on the roster because they have not paid for the correct class.",
        paused:
          "The wrestler's subscription is currently paused. They'll need to resume their subscription to check in.",
        overdue:
          "The wrestler has an overdue payment. They'll need to update their payment method to check in.",
        membership:
          "The wrestler's AAU or USAW membership is missing or expired. They'll need to record their USAW or AAU membership into WrestlingIQ to check in.",
      };
      return descriptions[this.slide3ActiveIssue];
    },
    currentIssuePlaceholder() {
      const placeholders = {
        roster: "Roster restriction modal",
        paused: "Subscription paused modal",
        overdue: "Subscription overdue modal",
        membership: "AAU/USAW required modal",
      };
      return placeholders[this.slide3ActiveIssue];
    },
    currentIssueGif() {
      const gifs = {
        roster: "https://mktg-cdn.wrestlingiq.com/kiosk/roster_missing.gif",
        paused:
          "https://mktg-cdn.wrestlingiq.com/kiosk/subscription_paused.gif",
        overdue:
          "https://mktg-cdn.wrestlingiq.com/kiosk/subscription_overdue.gif",
        membership: "https://mktg-cdn.wrestlingiq.com/kiosk/usaw_required.gif",
      };
      return gifs[this.slide3ActiveIssue];
    },
  },
};
</script>
